import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Copy, Check, Share2, X, Users, MousePointerClick, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ReferProviderModal — Mar 3, 2026
 * --------------------------------
 * Shared modal used by both the header chip AND the settings card. Fetches
 * /api/referrals/me on open, shows the personal invite link, copy button,
 * native share button (mobile), and a compact stats block:
 *   [ N clicks ]  [ N signups ]
 *   Recent signups: Jane D. · Mar 3   ·   John P. · Mar 1
 *
 * Pitch is explicitly non-monetary — "help a fellow provider chart faster".
 * No perks, no leaderboard, no unlocked features. Product decision.
 */
const ReferProviderModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const API = `${window.__WR_API_BASE__ || ''}/api`;
    axios.get(`${API}/referrals/me`, { withCredentials: true })
      .then(res => { if (!cancelled) { setData(res.data); } })
      .catch(err => {
        if (!cancelled) {
          if (err?.response?.status === 401) setError('Sign in first to get your invite link.');
          else setError('Could not load your invite link. Try again in a moment.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const copy = async (text, msg = 'Link copied') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(msg, { duration: 1500 });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
  };

  const nativeShare = async () => {
    if (!data) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'WriteRight PCM+',
          text: data.share_message,
          url: data.share_url,
        });
      } catch { /* user dismissed */ }
    } else {
      copy(data.share_message, 'Pitch + link copied');
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={() => onOpenChange(false)}
      data-testid="refer-provider-modal"
    >
      <div
        className="relative w-full max-w-md mx-4 rounded-xl border border-primary/30 bg-card shadow-2xl shadow-primary/10 p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Refer a Provider"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          aria-label="Close"
          data-testid="refer-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-foreground">Refer a fellow provider</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Share your link — help another PA, NP, MD, DO, or medic chart faster.
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="refer-modal-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Preparing your invite link…
          </div>
        )}

        {error && (
          <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="refer-modal-error">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Your invite link</label>
            <div className="flex gap-2 mb-3">
              <input
                readOnly
                value={data.share_url}
                onFocus={(e) => e.target.select()}
                className="flex-1 h-10 px-3 rounded-md bg-secondary/60 border border-border text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="refer-modal-link"
              />
              <button
                onClick={() => copy(data.share_url)}
                className={`h-10 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-primary hover:bg-primary/90 text-primary-foreground'}`}
                data-testid="refer-modal-copy-link"
              >
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>

            <button
              onClick={nativeShare}
              className="w-full h-10 rounded-md border border-primary/40 bg-primary/10 hover:bg-primary/15 text-primary font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors mb-4"
              data-testid="refer-modal-share"
            >
              <Share2 className="w-4 h-4" /> Share pitch + link
            </button>

            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2.5" data-testid="refer-stat-clicks">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <MousePointerClick className="w-3 h-3" /> Clicks
                  </div>
                  <div className="text-2xl font-bold text-foreground tabular-nums mt-0.5">{data.stats.clicks}</div>
                </div>
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5" data-testid="refer-stat-signups">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-emerald-300">
                    <Users className="w-3 h-3" /> Signups
                  </div>
                  <div className="text-2xl font-bold text-emerald-200 tabular-nums mt-0.5">{data.stats.signups}</div>
                </div>
              </div>

              {data.stats.recent_signups?.length > 0 ? (
                <div data-testid="refer-recent-signups">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Recent signups
                  </div>
                  <ul className="space-y-1">
                    {data.stats.recent_signups.map((r, i) => (
                      <li key={i} className="flex items-center justify-between text-xs text-foreground">
                        <span className="truncate">{r.referred_name || 'New provider'}</span>
                        <span className="text-muted-foreground text-[11px]">{fmtDate(r.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No signups yet — share the link and we&apos;ll show attributed sign-ups here as they land.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferProviderModal;
